/** Búsqueda de temas de ayuda: por id, por la pantalla en que se está y por lo que el usuario puede ver. */
import { matchPath } from 'react-router';
import { TEMAS } from './temas/index.ts';
import type { BloqueAyuda, PermisoAyuda, TemaAyuda } from './tipos.ts';

export { TEMAS };
const POR_ID = new Map(TEMAS.map((t) => [t.id, t]));

export const temaPorId = (id: string | null | undefined): TemaAyuda | null => (id ? (POR_ID.get(id) ?? null) : null);
export const hijosDe = (id: string): TemaAyuda[] => TEMAS.filter((t) => t.padre === id);

/** Un patrón con más partes fijas gana: «/cotizaciones/nueva» antes que «/cotizaciones/:id». */
const fijas = (patron: string) => patron.split('/').filter((p) => p && !p.startsWith(':')).length;

/** El tema de la pantalla actual (o null si la ruta no tiene ayuda propia). */
export function temaDeRuta(pathname: string): TemaAyuda | null {
  let mejor: { tema: TemaAyuda; puntos: number } | null = null;
  for (const tema of TEMAS) {
    for (const patron of tema.rutas ?? []) {
      if (!matchPath({ path: patron, end: true }, pathname)) continue;
      const puntos = fijas(patron);
      if (!mejor || puntos > mejor.puntos) mejor = { tema, puntos };
    }
  }
  return mejor?.tema ?? null;
}

type Puede = (clave: PermisoAyuda['clave'], nivel?: PermisoAyuda['nivel']) => boolean;
/**
 * Sin permiso pedido, lo ve todo el que entra; con permiso, quien lo tenga (cualquier nivel salvo que se pida otro); con
 * `sin: true`, solo quien NO lo tenga (la versión de un punto para los demás roles).
 */
export const visiblePara = (permiso: PermisoAyuda | undefined, puede: Puede) => {
  if (!permiso) return true;
  const tiene = puede(permiso.clave, permiso.nivel ?? 'lectura');
  return permiso.sin ? !tiene : tiene;
};

/** Todo el texto de un tema, para el buscador. */
const textosDeBloque = (b: BloqueAyuda) => (b.tipo === 'pasos' ? [b.titulo, ...b.pasos] : b.tipo === 'lista' ? [b.titulo, ...b.items] : [b.texto]);
/** Con `puede`, solo los bloques que ese usuario ve: el buscador no debe encontrar lo que luego no se muestra (la tasa 2). */
export function textoDe(t: TemaAyuda, puede?: Puede): string {
  const bloques = puede ? t.bloques.filter((b) => visiblePara(b.permiso, puede)) : t.bloques;
  return [t.titulo, t.resumen, ...(t.claves ?? []), ...bloques.flatMap(textosDeBloque), ...(t.mensajes ?? []).flatMap((m) => [m.mensaje, m.que_hacer])].join(' ');
}

/** Para buscar sin que importen acentos ni mayúsculas. */
export const normalizar = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
